import React from 'react';
import {
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaCode,
  FaEdit,
  FaFileCode,
  FaHistory,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';

const getTitleSizeClass = (title = '') => {
  const titleLength = String(title).length;

  if (titleLength > 58) return 'is-dense';
  if (titleLength > 38) return 'is-compact';
  return 'is-default';
};

function XsdTableMobile({
  xsds = [],
  loading = false,
  expandedIds = {},
  expandedVersionIds = {},
  onToggleExpand,
  onToggleVersionExpand,
  onEditDraft,
  onCreateVersion,
  onAddVersion,
  onShowHistory,
  onViewXml,
  onDeleteVersion,
  onGenerateCertificate,
  hasSavedCertificateDesign,
  renderNodeDetails,
}) {
  if (loading) {
    return <div className="xsd-mobile-message">Cargando esquemas XSD...</div>;
  }

  if (xsds.length === 0) {
    return <div className="xsd-mobile-message">No se encontraron esquemas XSD coincidentes.</div>;
  }

  return (
    <div className="xsd-mobile-list">
      {xsds.map((entity) => {
        const versions = Array.isArray(entity.versions) ? entity.versions : [];
        const entityDraft = versions.find((version) => version.status === 'DRAFT');
        const hasCertificateDesign = hasSavedCertificateDesign(entity.id);
        const hasScheduled = versions.some(
          (version) => version.effectiveDate && new Date(version.effectiveDate) > new Date()
        );
        const isExpanded = Boolean(expandedIds[entity.id]);

        return (
          <article key={entity.id} className="xsd-mobile-card">
            <div className="xsd-mobile-card-header">
              <div className="xsd-mobile-card-title">
                <span className="xsd-mobile-card-icon">
                  <FaFileCode />
                </span>
                <div className="min-w-0">
                  <h2 className={getTitleSizeClass(entity.name)}>{entity.name}</h2>
                  <p>{versions.length} versión{versions.length !== 1 ? 'es' : ''} registrada{versions.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button
                type="button"
                className="xsd-mobile-expand-button"
                onClick={() => onToggleExpand(entity.id)}
                aria-label={isExpanded ? 'Contraer versiones' : 'Expandir versiones'}
                title={isExpanded ? 'Contraer versiones' : 'Expandir versiones'}
              >
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>

            <div className="xsd-mobile-status-list">
              <span className={entity.status ? 'xsd-mobile-status is-active' : 'xsd-mobile-status is-inactive'}>
                {entity.activeVersion ? `${entity.activeVersion} (Vigente)` : 'Sin versión vigente'}
              </span>
              {entityDraft && (
                <span className="xsd-mobile-status is-draft">
                  <FaEdit /> Borrador ({entityDraft.version})
                </span>
              )}
              {hasScheduled && (
                <span className="xsd-mobile-status is-scheduled">
                  <FaClock /> Programada
                </span>
              )}
            </div>

            <div className="xsd-mobile-card-actions">
              <button
                type="button"
                className="xsd-mobile-action is-certificate"
                onClick={() => onGenerateCertificate(entity)}
                title={hasCertificateDesign ? 'Editar el diseño guardado para este XSD' : 'Crear un diseño de certificado para este XSD'}
              >
                <FaFileCode />
                {hasCertificateDesign ? 'Editar certificado' : 'Generar certificado'}
              </button>
              <button
                type="button"
                className={entityDraft ? 'xsd-mobile-action is-draft' : 'xsd-mobile-action is-primary'}
                onClick={() => (entityDraft ? onEditDraft(entity, entityDraft) : onAddVersion(entity))}
              >
                {entityDraft ? <FaEdit /> : <FaPlus />}
                {entityDraft ? 'Editar borrador' : 'Nueva versión'}
              </button>
              <button
                type="button"
                className="xsd-mobile-action is-secondary"
                onClick={() => onShowHistory(entity)}
              >
                <FaHistory />
                Trazabilidad ({(entity.events || []).length})
              </button>
            </div>

            {isExpanded && (
              <div className="xsd-mobile-versions">
                <div className="xsd-mobile-section-heading">
                  <strong>Versiones y estructura</strong>
                  <button type="button" onClick={() => onShowHistory(entity)}>
                    Historial
                  </button>
                </div>

                {versions.map((version, index) => {
                  const versionKey = version.version || `v${index + 1}`;
                  const versionId = `${entity.id}-${versionKey}`;
                  const versionExpanded = Boolean(expandedVersionIds[versionId]);
                  const isDraft = version.status === 'DRAFT';
                  const isScheduled = version.effectiveDate && new Date(version.effectiveDate) > new Date();

                  return (
                    <section
                      key={versionId}
                      className={`xsd-mobile-version ${isDraft ? 'is-draft' : ''}`}
                    >
                      <div className="xsd-mobile-version-header">
                        <div className="xsd-mobile-version-heading">
                          <strong>{version.version || `Versión ${index + 1}`}</strong>
                          <span>{Array.isArray(version.structure) ? version.structure.length : 0} nodos</span>
                        </div>
                        {isDraft ? (
                          <span className="xsd-mobile-version-badge is-draft"><FaEdit /> Borrador</span>
                        ) : isScheduled ? (
                          <span className="xsd-mobile-version-badge is-scheduled"><FaClock /> Programada</span>
                        ) : version.current ? (
                          <span className="xsd-mobile-version-badge is-current">Vigente</span>
                        ) : (
                          <span className="xsd-mobile-version-badge is-history">Histórica</span>
                        )}
                      </div>

                      <div className="xsd-mobile-version-actions">
                        <button
                          type="button"
                          className="xsd-mobile-action is-small is-primary"
                          onClick={() => (isDraft ? onEditDraft(entity, version) : onCreateVersion(entity, version))}
                        >
                          {isDraft ? <FaEdit /> : <FaPlus />}
                          {isDraft ? 'Editar' : 'Nueva desde esta'}
                        </button>
                        <button
                          type="button"
                          className="xsd-mobile-action is-small is-xml"
                          onClick={() => onViewXml(entity, version)}
                        >
                          <FaCode /> XML
                        </button>
                        {versions.length > 1 && (
                          <button
                            type="button"
                            className="xsd-mobile-icon-action is-danger"
                            onClick={() => onDeleteVersion(entity, versionKey)}
                            title="Eliminar versión"
                            aria-label="Eliminar versión"
                          >
                            <FaTrash />
                          </button>
                        )}
                        <button
                          type="button"
                          className="xsd-mobile-icon-action"
                          onClick={() => onToggleVersionExpand(entity.id, versionKey)}
                          title={versionExpanded ? 'Ocultar estructura' : 'Mostrar estructura'}
                          aria-label={versionExpanded ? 'Ocultar estructura' : 'Mostrar estructura'}
                        >
                          {versionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>

                      {!versionExpanded && (
                        <div className="xsd-mobile-version-meta">
                          <span>{isDraft ? 'Borrador en edición' : isScheduled ? `Entra en vigor: ${new Date(version.effectiveDate).toLocaleDateString()}` : version.current ? 'Activa' : 'Inactiva'}</span>
                        </div>
                      )}

                      {versionExpanded && (
                        <div className="xsd-mobile-version-detail">
                          {Array.isArray(version.structure) && version.structure.length > 0
                            ? version.structure.map((node, nodeIndex) => renderNodeDetails(node, [nodeIndex]))
                            : <div className="xsd-empty-state">Sin estructura definida.</div>}
                        </div>
                      )}
                    </section>
                  );
                })}

                <button type="button" className="xsd-mobile-add-version" onClick={() => onAddVersion(entity)}>
                  <FaPlus /> Agregar versión
                </button>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

export default XsdTableMobile;
